/* eslint-disable react/prop-types */
import * as React from "react";

import { CloseIco } from "../../assets/customIcons";
import FormFieldWrapper from "../FormField/FormFieldWrapper";
import IconButton from "../IButton/IconButton";
import cn from "classnames";
import styles from "./TextField.module.scss";

export type TextFieldProps = Omit<
  React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  >,
  "onChange"
> & {
  onChange?: (value: string) => void;
  label?: string;
  error?: string;
  isSearchField?: boolean;
  isClearable?: boolean;
};

export const TextField = React.memo(
  React.forwardRef(
    (
      props: TextFieldProps,
      ref: React.ForwardedRef<HTMLInputElement>,
    ): JSX.Element => {
      const {
        value,
        onChange,
        label,
        className,
        error,
        onFocus,
        isSearchField = false,
        isClearable = false,
        onBlur,
        ...rest
      } = props;

      const [focus, setIsFocus] = React.useState(false);

      const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocus(true);
        onFocus && onFocus(e);
      };

      const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocus(false);
        onBlur && onBlur(e);
      };

      const handleClear = React.useCallback(() => {
        onChange && onChange("");
      }, []);

      const isFocused = focus || !!value;

      return (
        <FormFieldWrapper
          label={label}
          value={value as string}
          isFocused={isFocused}
          className={cn(styles.textField, className)}
          error={error}
        >
          <input
            className={cn(styles.input, {
              [styles.active]: isFocused,
              [styles.error]: !!error,
            })}
            value={value}
            ref={ref}
            onChange={(e) => onChange(e.target.value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...rest}
          />
          {isClearable && value && (
            <CloseIco className={styles.close} onClick={handleClear} />
          )}
          {isSearchField && (
            <IconButton typeIcon="search" className={styles.search} />
          )}
        </FormFieldWrapper>
      );
    },
  ),
);
